import type { SubscriptionStatus } from '../api/platform.js';

/**
 * Shared subscription-status pill (Phase C-3). Replaces the two ad-hoc
 * active/amber badges that were inlined in platformTenants.tsx and
 * platformTenantDetail.tsx. Used by list + detail + the subscription panel.
 *
 * Tailwind palette note (apps/web/tailwind.config.js): emerald/amber/indigo
 * are SINGLE-hex tokens (use bare token or token/opacity — NOT `emerald-500`);
 * orange/red/gray are default scales (use `-NNN`). The classes below respect
 * that split — verified to render (a missing utility would yield an invisible
 * pill that jsdom tests can't catch).
 */
const STATUS_LABELS: Record<SubscriptionStatus, string> = {
  trial: 'Trial',
  active: 'Active',
  past_due: 'Past Due',
  read_only: 'Read-Only',
  suspended: 'Suspended',
  cancelled: 'Cancelled',
};

const STATUS_CLASSES: Record<SubscriptionStatus, string> = {
  trial: 'bg-indigo/10 text-indigo border-indigo/30',
  active: 'bg-emerald/10 text-emerald border-emerald/30',
  past_due: 'bg-amber/10 text-amber border-amber/30',
  read_only: 'bg-orange-500/10 text-orange-600 border-orange-500/30',
  suspended: 'bg-red-600/10 text-red-600 border-red-600/30',
  cancelled: 'bg-gray-500/10 text-gray-600 border-gray-400/40',
};

export function StatusBadge({
  status,
  size = 'md',
}: {
  status: SubscriptionStatus;
  size?: 'sm' | 'md';
}): JSX.Element {
  const sizeClasses = size === 'sm' ? 'text-xs px-2 py-0.5' : 'text-sm px-2.5 py-0.5';
  const colorClasses = STATUS_CLASSES[status] ?? STATUS_CLASSES.active;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border font-medium ${sizeClasses} ${colorClasses}`}
      aria-label={`Status: ${STATUS_LABELS[status]}`}
    >
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}
